// 涨停复盘 — 数据层。全部只读；后端 /api/v1/limit-up-review/*（外部 HTTP 0 次，不在请求里重算）。
//
// 日期纪律（2026-09-17 产品化）：页面选中的交易日是唯一的「那一天」，题材名单、全市场涨停名单
// 都按这个日期取。不带日期 = 最近一个已建台账的交易日。
import { useEffect, useRef } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient, useIsMutating } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type ThemeType = 'concept' | 'industry';
export type Tier = 'leader' | 'core' | 'laggard';

export interface Theme {
  theme_type: ThemeType; theme_name: string; member_count: number;
  member_count_truncated: boolean; sealed_count: number; touched_count: number;
  exclusive_weight: number | null; penetration: number | null; broke_rate: number | null;
  max_streak: number | null; leader_symbol: string | null; leader_name: string | null;
  core_count: number; laggard_count: number; unclassified_count: number;
  seat_count: number | null; rankable: boolean; flags: string[];
  // v2：分母来源 / 平均该封 / 行级提示 / 同批票折叠
  denominator_source: string | null; expected_count: number | null; surprise: number | null;
  notes: string[]; aliases: string[]; alias_of: string | null;
  primary_count: number | null;
  heat: { score: number | null; rank_in_day: number | null } | null;
}

export type RankKey = 'penetration' | 'sealed' | 'surprise' | 'heat';

export interface RankStat {
  rank_key: RankKey; bucket: string; horizon: number; n_days: number; n_legs: number;
  excess_pct: number | null; excess_t: number | null;
  vs_rest_pct: number | null; vs_rest_t: number | null;
  verdict: string; window_start: string | null; window_end: string | null;
}

export interface RankEvidence {
  rank_key: RankKey; verdict: 'forward_edge' | 'worse_than_rest' | 'no_forward_edge' | 'not_replayed';
  rows: RankStat[];
}

export interface Cluster {
  cluster_key: string; label: string; sealed_count: number; label_hits: number;
  member_count: number; penetration: number | null; max_streak: number | null;
  leader_symbol: string | null; leader_name: string | null; symbols: string[];
  bridge_symbols: string[]; ignition_labels: string[];
  symbol_names: Record<string, string>;
  // 每个标签一条明细：不是字符串数组，直接 join 会渲染成 [object Object]
  members: { theme_name: string; sealed: number; member_count: number;
             penetration: number | null; role: string; lift: number | null }[];
}

export interface DayResp {
  readTiming?: { durationMs: number; traceId?: string; serverTiming?: string; early?: boolean };
  ledger: {
    trade_date: string; membership_mode: string; sealed_count: number;
    touched_count: number; max_streak: number | null;
    coverage: { concept: number | null; industry: number | null; any: number | null };
    unclassified: { sealed: string[]; broken: string[] };
    reconcile: {
      pool_sealed_count: number | null; pool_total_count: number | null; status: string | null;
      delta: number | null; set_overlap: number | null; only_recomputed: number | null;
      only_pool: number | null; set_status: string | null;
    };
    themes_ranked: number; themes_broad_excluded: number; clusters_count: number;
    degraded: string[];
  };
  themes: Theme[]; main_board_total: number; single_stock: Theme[]; truncated: Theme[];
  broad_labels: Theme[]; low_base: Theme[]; clusters: Cluster[];
  degraded: string[]; rank_evidence: RankEvidence;
}

export interface DayListItem { trade_date: string; sealed_count: number | null; degraded: string[] }

export interface ThemeMember {
  review_return?: number | null;
  symbol: string; name: string | null; board_n: number | null; first_limit_time: string | null;
  sealed: boolean; touched: boolean; tier: Tier | null; tier_reason: string | null;
  has_seat: boolean | null; float_mv: number | null; theme_k: number; exclusive_share: number | null;
  reason_text: string | null; is_primary_reason: boolean | null;
}

export interface ThemeResp {
  membership_comparison?: {status: string; frame_members?: number; expected_touched?: number; ledger_touched?: number; only_in_ledger?: string[]; only_in_observed?: string[]} | null;
  membership_mode?: string; membership_verified?: boolean; membership_notice?: string;
  classification_bucket?: string; return_source?: string;
  theme_type: ThemeType; theme_name: string; latest_date: string;
  member_count: number; sealed_count: number; touched_count: number;
  penetration: number | null; max_streak: number | null; broke_rate: number | null; flags: string[];
  series: { trade_date: string; sealed_count: number; touched_count: number; penetration: number | null;
            max_streak: number | null; broke_rate: number | null }[];
  members: ThemeMember[];
}

export interface DayStock {
  symbol: string; name: string | null; board_n: number | null; first_limit_time: string | null;
  sealed: boolean; touched: boolean; has_seat: boolean | null; float_mv: number | null;
  classified: boolean; reason_text: string | null;
  themes: { theme_type: ThemeType; theme_name: string; tier: Tier | null; rankable: boolean;
            is_primary_reason: boolean | null }[];
}

export interface DayStocksResp {
  trade_date: string; sealed_count: number; touched_count: number; stocks: DayStock[]; degraded: string[];
}

// ── 分类来源（2026-10-22）：概念、行业各选一套统一分类库来源，每套来源一个台账桶 `cls:<source>`；
// 空来源 = 旧口径桶 replay_v3。选择存在后端（夜间日批据此滚动重算），换浏览器也记得。
export const LEGACY_EVI = 'replay_v3';
export const eviFor = (source: string) => (source ? `cls:${source}` : LEGACY_EVI);

/** 一次请求用到的两个桶。所有缓存键都带上它，换来源不会读到旧来源的结果。 */
export interface Buckets { concept: string; industry: string }

export interface BucketStatus {
  source: string; evi_id: string; latest_date: string | null; days_built: number;
  job: { status: 'idle' | 'queued' | 'running' | 'done' | 'failed'; error?: string | null; finished_at?: string };
}

export interface ClassificationPref {
  readTiming?: { durationMs: number; traceId?: string; serverTiming?: string; operation: 'read' | 'save' | 'bootstrap'; saveStartedAt?: number };
  concept: string; industry: string; saved: boolean;
  defaults: { concept: string; industry: string };
  buckets: { concept: BucketStatus; industry: BucketStatus };
  available_buckets?: Record<string, BucketStatus>;
  preparation?: { status: string; trade_date?: string; current_source?: string | null;
    sources?: Record<string, { status: string; error?: string }> };
}

export function useLurClassification() {
  const saving = useIsMutating({ mutationKey: ['lur', 'classification', 'save'] }) > 0;
  return useQuery({
    enabled: !saving,
    queryKey: ['lur', 'classification'],
    queryFn: async ({ signal }) => {
      const start = performance.now();
      const response = await api.get<ClassificationPref>('/limit-up-review/classification', { signal });
      return { ...response.data, readTiming: { durationMs: Math.round(performance.now() - start),
        traceId: response.headers?.['x-runtime-trace'], operation: 'read' as const } };
    },
    staleTime: 60_000,
    // 后台在建时轮询，建完停
    refetchInterval: (query) => {
      const b = query.state.data?.buckets;
      return ['queued', 'running'].includes(query.state.data?.preparation?.status ?? '') ||
        (b && [b.concept, b.industry].some((x) => ['queued', 'running'].includes(x.job.status))) ? 2000 : false;
    },
  });
}

export function useSaveLurClassification() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['lur', 'classification', 'save'],
    mutationFn: async (v: { concept: string; industry: string; trade_date?: string }) => {
      const start = performance.now();
      const response = await api.put<ClassificationPref>('/limit-up-review/classification', v);
      return { ...response.data, readTiming: { durationMs: Math.round(performance.now() - start),
        traceId: response.headers?.['x-runtime-trace'], serverTiming: response.headers?.['server-timing'], operation: 'save' as const } };
    },
    onMutate: async (choice) => {
      const saveStartedAt = performance.now();
      await qc.cancelQueries({ queryKey: ['lur', 'classification'] });
      const previous = qc.getQueryData<ClassificationPref>(['lur', 'classification']);
      const concept = previous?.available_buckets?.[choice.concept];
      const industry = previous?.available_buckets?.[choice.industry];
      // The explicit bucket parameters can be read while preferences save. Use
      // factual server statuses only; failure rolls back the displayed selection.
      if (previous && concept && industry) qc.setQueryData(['lur', 'classification'], {
        ...previous, concept: choice.concept, industry: choice.industry,
        buckets: { concept, industry },
      });
      return { previous, saveStartedAt };
    },
    onSuccess: (data, _choice, context) => qc.setQueryData(['lur', 'classification'], {
      ...data, readTiming: { ...data.readTiming!, saveStartedAt: context?.saveStartedAt },
    }),
    onError: (_error, _choice, context) => {
      if (context?.previous) qc.setQueryData(['lur', 'classification'], context.previous);
    },
  });
}

export function usePrepareLurSources() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post('/limit-up-review/classification/prepare')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lur', 'classification'] }),
  });
}

export function useBuildBucket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (value: string | { source: string; date?: string; preview_only?: boolean }) =>
      (await api.post<BucketStatus>('/limit-up-review/classification/build', null,
        { params: typeof value === 'string' ? { source: value } : value })).data,
    onSettled: () => qc.invalidateQueries({ queryKey: ['lur', 'classification'] }),
  });
}

export function useDays(evi: string, enabled = true) {
  return useQuery({
    queryKey: ['lur', 'days', evi],
    queryFn: async ({ signal }) => (await api.get<{ days: DayListItem[] }>('/limit-up-review/days', { signal, params: { limit: 250, evi_id: evi } })).data,
    enabled,
    staleTime: 10 * 60_000,
  });
}

/** Invalidate only when a bucket's materialized outputs advance, not when selected. */
export function useLurBucketUpdates(pref: ClassificationPref | undefined) {
  const qc = useQueryClient();
  const versions = useRef(new Map<string, string>());
  useEffect(() => {
    if (!pref) return;
    const statuses = Object.values(pref.available_buckets ?? pref.buckets);
    const changed = new Set<string>();
    for (const b of statuses) {
      const version = `${b.days_built}:${b.latest_date ?? ''}:${b.job.finished_at ?? ''}`;
      const previous = versions.current.get(b.evi_id);
      if (previous !== undefined && previous !== version) changed.add(b.evi_id);
      versions.current.set(b.evi_id, version);
    }
    if (changed.size) void qc.invalidateQueries({ predicate: ({ queryKey: k }) =>
      k[0] === 'lur' && ((k[1] === 'days' && changed.has(String(k[2]))) ||
        (k[1] === 'day' && [k[5], k[6]].some(e => changed.has(String(e))))) });
  }, [pref, qc]);
}

export function lurDayQuery(date: string | null, sort: string, themeType: ThemeType, buckets: Buckets | null) {
  return {
    queryKey: ['lur', 'day', date ?? 'latest', sort, themeType, buckets?.concept, buckets?.industry],
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<DayResp> => {
      const start = performance.now();
      const response = await api.get<DayResp>('/limit-up-review/day', {
        signal, params: { sort, theme_type: themeType, date: date ?? undefined,
          concept_evi_id: buckets!.concept, industry_evi_id: buckets!.industry },
      });
      return { ...response.data, readTiming: { durationMs: Math.round(performance.now() - start),
        traceId: response.headers?.['x-runtime-trace'], serverTiming: response.headers?.['server-timing'] } };
    },
    retry: false,
    staleTime: 5 * 60_000,
  };
}

export function useDay(date: string | null, sort: string, themeType: ThemeType, buckets: Buckets | null) {
  return useQuery({
    ...lurDayQuery(date, sort, themeType, buckets),
    enabled: buckets !== null,
    // 只在同一组桶内保留上一份（翻日期/排序）；换来源不把旧来源的榜留在屏上
    placeholderData: (prev, prevQuery) =>
      prevQuery && prevQuery.queryKey[5] === buckets?.concept && prevQuery.queryKey[6] === buckets?.industry
        ? keepPreviousData(prev) : undefined,
  });
}

/** Visible result first; warm recent materialized reads one at a time, never build data.
 * The two neighbors lead the queue. Other ready sources at this date and the
 * remaining 60-day window follow, so a date-picker jump can also hit memory.
 */
export function usePrefetchLurDates(days: DayListItem[] | undefined, current: string | undefined,
  sort: string, themeType: ThemeType, buckets: Buckets | null, enabled: boolean, pref?: ClassificationPref) {
  const qc = useQueryClient();
  const concept = buckets?.concept, industry = buckets?.industry;
  // Stable factual statuses: preference saves/polls alone must not restart warming.
  const readySources = Object.values(pref?.available_buckets ?? {})
    .filter(b => b.days_built > 0 && b.latest_date && b.latest_date >= (current ?? '') &&
      !['queued', 'running', 'failed'].includes(b.job.status))
    .map(b => b.evi_id).sort().join('|');
  useEffect(() => {
    if (!enabled || !concept || !industry || !current || !days) return;
    const index = days.findIndex(d => d.trade_date === current);
    if (index < 0) return;
    const adjacent = [days[index + 1], days[index - 1]].filter((d): d is DayListItem => !!d);
    const cutoff = new Date(`${days[0].trade_date}T00:00:00Z`);
    cutoff.setUTCDate(cutoff.getUTCDate() - 60);
    const recent = days.filter(d => d.trade_date >= cutoff.toISOString().slice(0, 10)).slice(0, 60);
    const targets = adjacent.map(d => ({ date: d.trade_date, concept, industry }));
    for (const evi of readySources.split('|').filter(Boolean)) {
      if (evi !== buckets![themeType]) targets.push({ date: current,
        concept: themeType === 'concept' ? evi : concept,
        industry: themeType === 'industry' ? evi : industry });
    }
    // Without a server status manifest preserve the small, neighbor-only contract.
    if (pref) targets.push(...recent.filter(d => d.trade_date !== current)
      .map(d => ({ date: d.trade_date, concept, industry })));
    const seen = new Set<string>();
    let stopped = false;
    let pending: ReturnType<typeof lurDayQuery>['queryKey'] | undefined;
    void (async () => {
      for (const d of targets) {
        if (stopped) break;
        // DateNav represents the newest option with the latest (null) query.
        const active = d[themeType];
        const activeStatus = Object.values(pref?.available_buckets ?? {}).find(b => b.evi_id === active);
        const sameBucket = active === buckets![themeType];
        const target = d.date === days[0]?.trade_date && (sameBucket || activeStatus?.latest_date === d.date)
          ? null : d.date;
        const options = lurDayQuery(target, sort, themeType, d);
        const identity = JSON.stringify(options.queryKey);
        if (seen.has(identity)) continue;
        seen.add(identity);
        // Do not take ownership of another reader's in-flight request.
        if (qc.getQueryState(options.queryKey)?.fetchStatus === 'fetching') continue;
        pending = options.queryKey;
        await qc.prefetchQuery(options);
        pending = undefined;
        // A failed source stays an error; do not retry/fan out during an outage.
        if (qc.getQueryState(options.queryKey)?.status === 'error') break;
      }
    })();
    return () => {
      stopped = true;
      // A prefetched neighbor may have become the visible date: let that read finish.
      if (pending) {
        const key = pending;
        // React installs the new date's observer after the old effect cleanup.
        queueMicrotask(() => { void qc.cancelQueries({ queryKey: key, exact: true, type: 'inactive' }); });
      }
    };
  }, [qc, days, current, sort, themeType, concept, industry, enabled, readySources, !!pref]);
}

export function useTheme(date: string | null, type: ThemeType, name: string | null, evi: string) {
  return useQuery({
    queryKey: ['lur', 'theme', date, type, name, evi],
    queryFn: async () => (await api.get<ThemeResp>('/limit-up-review/theme', {
      params: { name, theme_type: type, date: date ?? undefined, days: 30, evi_id: evi },
    })).data,
    enabled: name !== null,
    staleTime: 5 * 60_000,
  });
}

export function useDayStocks(date: string | null, enabled: boolean, buckets: Buckets) {
  return useQuery({
    queryKey: ['lur', 'day-stocks', date ?? 'latest', buckets.concept, buckets.industry],
    queryFn: async () => (await api.get<DayStocksResp>('/limit-up-review/day/stocks', {
      params: { date: date ?? undefined, concept_evi_id: buckets.concept, industry_evi_id: buckets.industry },
    })).data,
    enabled,
    staleTime: 5 * 60_000,
  });
}
